import { Temporal } from '@js-temporal/polyfill'

import { calendarDateInTimeZone, isCalendarDate } from './calendar-date'

export type ClockOccurrence = 'earlier' | 'later'

function validateZone(timeZone: string): void {
  // Application preferences are named zones, not fixed offsets. Never replace
  // an invalid explicit zone with UTC inside a conversion.
  if (!timeZone || /^[+-]/.test(timeZone))
    throw new RangeError('Invalid time zone')
  new Intl.DateTimeFormat('en-US', { timeZone })
}

function parseDay(day: string): Temporal.PlainDate {
  if (!isCalendarDate(day)) throw new RangeError('Invalid calendar date')
  return Temporal.PlainDate.from(day)
}

/**
 * Advance an automatic business period by calendar months in its named zone.
 * Clamp missing month-end dates; move skipped clocks forward by the gap and
 * choose the earlier occurrence of repeated clocks. Preserve sub-minute precision.
 * For recurring anniversaries, pass the original anchor and total month offset
 * rather than chaining clamped results (January 31 + 2 months is March 31).
 */
export function addCalendarMonthsInTimeZone(
  instant: Date,
  months: number,
  timeZone: string,
): Date {
  validateZone(timeZone)
  if (!Number.isSafeInteger(months)) throw new RangeError('Invalid month count')
  const zoned = Temporal.Instant.fromEpochMilliseconds(
    instant.getTime(),
  ).toZonedDateTimeISO(timeZone)
  if (months === 0) return new Date(instant.getTime())
  const target = zoned
    .toPlainDateTime()
    .add({ months }, { overflow: 'constrain' })
  return new Date(
    target.toZonedDateTime(timeZone, { disambiguation: 'compatible' })
      .epochMilliseconds,
  )
}

/** Clock display of an instant; independent of the machine's timezone. */
export function clockInTimeZone(instant: Date, timeZone: string): string {
  validateZone(timeZone)
  return Temporal.Instant.fromEpochMilliseconds(instant.getTime())
    .toZonedDateTimeISO(timeZone)
    .toPlainTime()
    .toString({ smallestUnit: 'minute' })
}

/** Repeat an existing local clock on another calendar day. Keep the original
 * instant (including a chosen fold occurrence) on its anchor day. Subsequent
 * occurrences follow schedule rules: advance gaps and use the earlier fold.
 * Seconds and milliseconds are retained. */
export function instantOnCalendarDate(
  instant: Date,
  day: string,
  timeZone: string,
): Date {
  validateZone(timeZone)
  const date = parseDay(day)
  const original = Temporal.Instant.fromEpochMilliseconds(
    instant.getTime(),
  ).toZonedDateTimeISO(timeZone)
  if (original.toPlainDate().equals(date)) return new Date(instant.getTime())
  return new Date(
    date
      .toPlainDateTime(original.toPlainTime())
      .toZonedDateTime(timeZone, { disambiguation: 'compatible' })
      .epochMilliseconds,
  )
}

export interface ClockCandidate {
  instant: Date
  offset: string
}

/** Recurring schedule clocks use compatible DST resolution: advance gaps and
 * choose the earlier occurrence of folds. Explicit time-entry forms still use
 * clockCandidates so a person can choose an occurrence. */
export function scheduleClockInstant(
  day: string,
  clock: string,
  timeZone: string,
): Date {
  validateZone(timeZone)
  parseDay(day)
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(clock))
    throw new RangeError('Invalid clock time')
  return new Date(
    Temporal.PlainDateTime.from(`${day}T${clock}`).toZonedDateTime(timeZone, {
      disambiguation: 'compatible',
    }).epochMilliseconds,
  )
}

/**
 * No candidates means the clock was skipped; two means it repeated. Callers
 * must let the user resolve a repeated clock rather than silently choosing it.
 */
export function clockCandidates(
  day: string,
  clock: string,
  timeZone: string,
): ClockCandidate[] {
  validateZone(timeZone)
  parseDay(day)
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(clock))
    throw new RangeError('Invalid clock time')
  const local = Temporal.PlainDateTime.from(`${day}T${clock}`)
  const earlier = local.toZonedDateTime(timeZone, { disambiguation: 'earlier' })
  const later = local.toZonedDateTime(timeZone, { disambiguation: 'later' })
  const candidates =
    earlier.epochMilliseconds === later.epochMilliseconds
      ? [earlier]
      : [earlier, later]
  return candidates
    .filter(candidate => candidate.toPlainDateTime().equals(local))
    .map(candidate => ({
      instant: new Date(candidate.epochMilliseconds),
      offset: candidate.offset,
    }))
}

/**
 * Earliest instant belonging to the day, including midnight gaps/folds. A
 * completely skipped date has the same boundary as the following date.
 */
export function startOfCalendarDay(day: string, timeZone: string): Date {
  validateZone(timeZone)
  return new Date(parseDay(day).toZonedDateTime(timeZone).epochMilliseconds)
}

/** Inclusive calendar dates become a half-open instant range, never +24h. */
export function calendarDayRange(
  startDay: string,
  endDay: string,
  timeZone: string,
): { start: Date; endExclusive: Date } {
  const startDate = parseDay(startDay)
  const endDate = parseDay(endDay)
  if (Temporal.PlainDate.compare(startDate, endDate) > 0)
    throw new RangeError('End date must not precede start date')
  return {
    start: startOfCalendarDay(startDay, timeZone),
    endExclusive: startOfCalendarDay(
      endDate.add({ days: 1 }).toString(),
      timeZone,
    ),
  }
}

/** Inclusive business deadline, including short/long days and midnight changes. */
export function endOfCalendarDay(day: string, timeZone: string): Date {
  const { start, endExclusive } = calendarDayRange(day, day, timeZone)
  if (start.getTime() >= endExclusive.getTime())
    throw new RangeError('This calendar date does not exist in this time zone')
  return new Date(endExclusive.getTime() - 1)
}

export interface EntryClock {
  day: string
  clock: string
  timezone: string
  original?: Date | string | null
  occurrence?: ClockOccurrence
}

export interface ResolvedEntryClock {
  instant: Date | null
  candidates: ClockCandidate[]
  occurrence?: ClockOccurrence
  error?: string
}

/** Preserve seconds and the original occurrence when the displayed clock is unchanged. */
export function resolveEntryClock(input: EntryClock): ResolvedEntryClock {
  if (!input.clock || !input.day) return { instant: null, candidates: [] }
  let candidates: ClockCandidate[]
  try {
    candidates = clockCandidates(input.day, input.clock, input.timezone)
  } catch {
    return {
      instant: null,
      candidates: [],
      error: 'Enter a valid date and time.',
    }
  }
  if (candidates.length === 0) {
    return {
      instant: null,
      candidates,
      error: `This time does not exist on ${input.day} in ${input.timezone} because the clocks change. Choose another time.`,
    }
  }

  const original = input.original ? new Date(input.original) : null
  const matchesOriginal =
    original &&
    Number.isFinite(original.getTime()) &&
    calendarDateInTimeZone(original, input.timezone) === input.day &&
    clockInTimeZone(original, input.timezone) === input.clock
  const originalIndex = matchesOriginal
    ? candidates.findIndex(
        candidate =>
          original.getTime() >= candidate.instant.getTime() &&
          original.getTime() < candidate.instant.getTime() + 60_000,
      )
    : -1
  const selectedIndex =
    candidates.length === 1
      ? 0
      : input.occurrence
        ? input.occurrence === 'earlier'
          ? 0
          : 1
        : originalIndex
  if (selectedIndex < 0) {
    return {
      instant: null,
      candidates,
      error:
        'This clock time occurs twice. Choose the first or second occurrence.',
    }
  }
  const selected = candidates[selectedIndex]
  if (!selected)
    return {
      instant: null,
      candidates,
      error: 'Choose a valid clock occurrence.',
    }
  return {
    instant: selectedIndex === originalIndex ? original : selected.instant,
    candidates,
    ...(candidates.length === 2
      ? {
          occurrence:
            selectedIndex === 0 ? ('earlier' as const) : ('later' as const),
        }
      : {}),
  }
}

export function resolveEntryClockRange(
  start: EntryClock,
  end: EntryClock,
  pausedSeconds = 0,
) {
  const resolvedStart = resolveEntryClock(start)
  const resolvedEnd = resolveEntryClock(end)
  const error = resolvedStart.error || resolvedEnd.error
  if (error || !resolvedStart.instant || !resolvedEnd.instant) {
    return { start: resolvedStart, end: resolvedEnd, hours: 0, error }
  }
  if (!Number.isFinite(pausedSeconds) || pausedSeconds < 0) {
    return {
      start: resolvedStart,
      end: resolvedEnd,
      hours: 0,
      error: 'Invalid paused time.',
    }
  }
  const elapsedSeconds =
    (resolvedEnd.instant.getTime() - resolvedStart.instant.getTime()) / 1000
  const hours = (elapsedSeconds - Math.max(0, pausedSeconds)) / 3600
  return {
    start: resolvedStart,
    end: resolvedEnd,
    hours: Math.max(0, hours),
    error:
      hours <= 0
        ? 'End time must be after start time and any paused time.'
        : undefined,
  }
}

/** Adding elapsed hours can cross a clock change or midnight. Retain its occurrence. */
export function endClockFromHours(
  start: Date,
  hours: number,
  timezone: string,
  pausedSeconds = 0,
) {
  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(hours) ||
    hours <= 0 ||
    !Number.isFinite(pausedSeconds) ||
    pausedSeconds < 0
  )
    throw new RangeError('Invalid duration')
  const instant = new Date(
    start.getTime() + (hours * 3600 + Math.max(0, pausedSeconds)) * 1000,
  )
  if (!Number.isFinite(instant.getTime()))
    throw new RangeError('Duration is out of range')
  const day = calendarDateInTimeZone(instant, timezone)
  if (!day) throw new RangeError('Duration is out of range')
  const clock = clockInTimeZone(instant, timezone)
  return {
    day,
    clock,
    instant,
    occurrence: resolveEntryClock({ day, clock, timezone, original: instant })
      .occurrence,
  }
}

/** A description-only edit must not trigger the API's clock recalculation. */
export function clockUpdateFields(
  existing: {
    startTime?: Date | string | null
    endTime?: Date | string | null
  },
  startTime: Date | null,
  endTime: Date | null,
  duration: number | null,
  hoursEdited: boolean,
): {
  startTime?: Date | null
  endTime?: Date | null
  duration?: number | null
} {
  const same = (
    stored: Date | string | null | undefined,
    current: Date | null,
  ) =>
    stored
      ? current?.getTime() === new Date(stored).getTime()
      : current === null
  if (
    same(existing.startTime, startTime) &&
    same(existing.endTime, endTime) &&
    ((startTime && endTime) || !hoursEdited)
  )
    return {}
  return { startTime, endTime, duration }
}

export {
  nextScheduleCadenceRun,
  assertScheduleConfig,
} from './schedule-cadence'
export type { ScheduleCadence } from './schedule-cadence'

export {
  scheduledActionInstant,
  resolveWorkflowActionTime,
  workflowActionScheduleConfig,
} from './workflow-action-schedule'

export {
  WORKFLOW_TIME_UNITS,
  WORKFLOW_CALENDAR_UNITS,
  workflowTimeZone,
  workflowTimeZoneFromSettings,
  isWorkflowTimeExpression,
  parseWorkflowTimeExpression,
  getWorkflowTimeExpressionBasePath,
  resolveWorkflowTimeDate,
  resolveWorkflowTimeCalendarDate,
  workflowTimeTokens,
  assertWorkflowTimeTokens,
} from './workflow-time'
export type { WorkflowTimeUnit, WorkflowTimeOptions } from './workflow-time'

export {
  workflowTemporalKind,
  evaluateWorkflowTemporalCondition,
  assertWorkflowTemporalConditions,
  WorkflowTemporalConditionError,
} from './workflow-condition-time'
export type { WorkflowTemporalKind } from './workflow-condition-time'
